/**
 * The page restore guard: nothing of a client's comes back on a page the
 * browser restores after a sign-out, and other open tabs follow a sign-out.
 *
 * A page restored from the back-forward cache comes back with its DOM and
 * its memory, every client record it had fetched included. On a `pageshow`
 * whose `persisted` is true the guard hides the document at once, asks the
 * platform who is signed in (`/.auth/me`, no-store), and shows the page
 * again only when it is the same person this page was loaded for. Anything
 * else (nobody, someone else, a failed or slow check, a page whose person
 * was never known) clears this tab's session storage and reloads, leaving
 * the page hidden; the fresh document boots and shows the sign-in screen
 * when nobody is signed in.
 *
 * The second listener is the cross-tab signal: a `storage` event that
 * removes or changes the signed-in key (src/lib/signedInStorage.ts) to
 * someone other than this page's person, or clears the whole store, hides
 * the page, clears this tab's session storage and reloads. Events are
 * ignored until this page's person is known: a tab still booting settles
 * through its own claim.
 *
 * Installed once per document by src/main.tsx; App tells it whose page it
 * is with `rememberPageUser` when `/.auth/me` settles.
 */

import { fetchPrincipal } from '@/lib/auth';
import { SIGNED_IN_USER_KEY, clearSessionStorage } from '@/lib/signedInStorage';

/** A restore check that has not settled by then counts as failed. */
export const RESTORE_CHECK_TIMEOUT_MS = 5000;

/** This page's person: undefined until the boot has asked, null when the
 *  page was loaded with nobody signed in. */
let pageUser: string | null | undefined;

const installed = new Set<() => void>();

export function rememberPageUser(userId: string | null): void {
  pageUser = userId;
}

/** This page's person, or null when not (yet) known. */
export function pageUserId(): string | null {
  return pageUser ?? null;
}

/** Hide the whole page. `display: none` on the root element with priority
 *  `important`: nothing inside can show through it. */
export function hideDocument(): void {
  document.documentElement.style.setProperty('display', 'none', 'important');
}

export function showDocument(): void {
  document.documentElement.style.removeProperty('display');
}

/** Who the platform says is signed in, or undefined when the check failed
 *  or did not settle within the limit. */
async function signedInUserWithinLimit(): Promise<string | null | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limit = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), RESTORE_CHECK_TIMEOUT_MS);
  });
  const check = fetchPrincipal().then(
    (principal) => principal?.userId ?? null,
    () => undefined,
  );
  try {
    return await Promise.race([check, limit]);
  } finally {
    clearTimeout(timer);
  }
}

export interface PageRestoreGuardDeps {
  reload?: () => void;
}

export function installPageRestoreGuard(deps: PageRestoreGuardDeps = {}): () => void {
  const reload = deps.reload ?? (() => window.location.reload());

  const leave = (): void => {
    clearSessionStorage();
    reload();
  };

  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted !== true) return;
    hideDocument();
    const expected = pageUser;
    void signedInUserWithinLimit().then((current) => {
      if (typeof expected === 'string' && current === expected) {
        showDocument();
      } else {
        leave();
      }
    });
  };

  const onStorage = (event: StorageEvent): void => {
    if (pageUser === undefined) return;
    const signalChanged = event.key === SIGNED_IN_USER_KEY && event.newValue !== pageUser;
    if (event.key !== null && !signalChanged) return;
    hideDocument();
    leave();
  };

  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('storage', onStorage);
  const remove = (): void => {
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('storage', onStorage);
    installed.delete(remove);
  };
  installed.add(remove);
  return remove;
}

/** Tests only: forget this page's person and remove the listeners. */
export function resetPageRestoreGuardForTests(): void {
  pageUser = undefined;
  for (const remove of [...installed]) remove();
}
